import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import agentData from '../../../../../../constants/agents/initialStates/agentData';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setAgentData,
  setAgentEtc,
  setAIAgentData,
  setPrevAgentStep,
  setQualificationAgentData,
  setSmartReplyData,
} from '../../../../../../redux/slices/agents/agentSlice';
import { getAgentState } from '../../../../../../constants/agents/initialStates/agentState';
import {
  useCreateSmartReplyMutation,
  useUpdateSmartReplyMutation,
} from '../../../../../../services/api';
import useEventScope from '../../../../../events/controllers/useEventScope';
import { getEventContextPath } from '../../../../../events/controllers/eventFormatters';
import {
  withEventScopeForCreate,
  withEventScopeForUpdate,
} from '../../../../../events/controllers/eventScopePayload';

const useSmartReplyHandler = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const smartReply = useSelector(state => state?.agent?.smartReply);
  const creationType = useSelector(state => state.agent.etc.creationType);
  const brandingPrimaryColor = useSelector(state => state.branding.branding.primaryColor);
  const search = useLocation().search;
  const redirect = new URLSearchParams(search).get('redirect');
  const _id = new URLSearchParams(search).get('_id');
  const { isEventContext, eventId, workflowId, workflowKey } = useEventScope();

  const [_CREATE_SMART_REPLY, { isLoading: isCreating }] = useCreateSmartReplyMutation();
  const [_UPDATE_SMART_REPLY, { isLoading: isUpdating }] = useUpdateSmartReplyMutation();

  const onPrevHandler = () => {
    dispatch(setPrevAgentStep());
  };

  const prepareAgentData = () => {
    const protocol = smartReply?.smartReplyData?.protocol;
    let smartReplayProtocol;

    // Build a new object — do not mutate/delete on Redux state (Immer freezes it in dev; delete throws).
    if (protocol?.type === 'imap') {
      const { clientId, clientSecret, authority, ...imapBase } = protocol;
      smartReplayProtocol = {
        ...imapBase,
        host: protocol?.host,
        port: protocol?.port,
        username: protocol?.username,
        password: protocol?.password,
        secure: protocol?.secure,
        email: protocol?.email,
      };
    } else if (protocol?.type === 'graphAPI') {
      const { host, port, username, secure, ...graphBase } = protocol;
      smartReplayProtocol = {
        ...graphBase,
        clientId: protocol?.clientId,
        clientSecret: protocol?.clientSecret,
        authority: protocol?.authority,
        email: protocol?.email,
      };
    } else if (protocol?.type === 'freshdesk') {
      smartReplayProtocol = {
        type: 'freshdesk',
        apiKey: protocol?.apiKey ?? '',
        domain: protocol?.domain ?? '',
        email: protocol?.email ?? '',
      };
    } else {
      smartReplayProtocol = protocol ? { ...protocol } : protocol;
    }

    // smart reply email configs
    const smartReplyEmailConfigs = {
      protocol: {
        ...smartReplayProtocol,
      },
      emailTemplate: {
        ...smartReply?.smartReplyData?.emailTemplate,
        renderFormat: smartReply?.smartReplyData?.emailTemplate?.renderFormat,
        footer: smartReply?.smartReplyData?.emailTemplate?.footer,
      },
      replyApprovalConfig: {
        autoApproval: smartReply?.smartReplyData?.replyApprovalConfig?.autoApproval,
        recipients: smartReply?.smartReplyData?.replyApprovalConfig?.autoApproval
          ? []
          : smartReply?.smartReplyData?.replyApprovalConfig?.recipients,
      },
    };

    // smart reply whatsapp configs — Meta links via integrationId
    const smartReplyWhatsappConfigs = {
      integrationId: smartReply?.smartReplyData?.integrationId,
    };

    return {
      ...smartReply,
      smartReplyData: {
        type: smartReply?.smartReplyData?.type,
        engineId: smartReply?.smartReplyData?.engineId?._id,
        actionBucketIds: smartReply?.smartReplyData?.actionBucketIds?.map(bucket => bucket?._id),
        ...(smartReply?.smartReplyData?.type === 'email'
          ? smartReplyEmailConfigs
          : smartReply?.smartReplyData?.type === 'whatsapp'
            ? smartReplyWhatsappConfigs
            : {}),
      },
    };
  };

  const onSubmitHandler = () => {
    switch (creationType) {
      case 'create':
        return createAgent();
      case 'edit':
        return editAgent();
      default:
        return createAgent();
    }
  };

  const createAgent = async () => {
    try {
      const payload = withEventScopeForCreate(prepareAgentData(), {
        isEventContext,
        eventId,
        workflowId,
      });
      const response = await _CREATE_SMART_REPLY(payload);

      if (response?.data?.success) {
        toast.success('Agent created');

        clearAgentData();
        if (redirect === 'overview') {
          navigate(`${agentData?.overviewPath}?_id=${_id}&isSmartReply=true`);
        } else {
          navigate(
            isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
          );
        }
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't create the agent"
        );
      }
    } catch (error) {
      toast.error("Couldn't create the agent");
    }
  };

  const editAgent = async () => {
    try {
      const payload = withEventScopeForUpdate(
        prepareAgentData(),
        { isEventContext, eventId, workflowId },
        { eventId: smartReply?.eventId, workflowId: smartReply?.workflowId }
      );
      const response = await _UPDATE_SMART_REPLY(payload);

      if (response?.data?.success) {
        toast.success('Changes saved');

        clearAgentData();
        if (redirect === 'overview') {
          navigate(`${agentData?.overviewPath}?_id=${_id}&isSmartReply=true`);
        } else {
          navigate(
            isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
          );
        }
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't save the changes"
        );
      }
    } catch (error) {
      toast.error("Couldn't save the changes");
    }
  };

  const clearAgentData = () => {
    const agentState = getAgentState(brandingPrimaryColor);
    dispatch(setAgentEtc({ ...agentState.etc, creationType: 'create' }));
    dispatch(setAgentData(agentState.data));
    dispatch(setAIAgentData(agentState.aiAgent));
    dispatch(setSmartReplyData(agentState.smartReply));
    dispatch(setQualificationAgentData(agentState.qualificationAgent));
  };

  return { onPrevHandler, onSubmitHandler, isCreating, isUpdating };
};

export default useSmartReplyHandler;
